import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.min.css';
import { initLucideIcons } from './lucide-icons';

const CROP_ACTION_NAMESPACE = '.naisFileInputCropper';

export type CropperModalOptions = {
    aspectRatio?: number;
    outputHeight?: number;
    outputMimeType?: string;
    outputQuality?: number;
    outputWidth?: number;
};

function getPreviewData($input: JQuery<HTMLInputElement>): Record<string, unknown> {
    try {
        return ($input.fileinput('getPreview') || {}) as Record<string, unknown>;
    } catch {
        return {};
    }
}

function getPreviewConfig(previewData: Record<string, unknown>, index: number): Record<string, unknown> {
    const config = Array.isArray(previewData.config) ? previewData.config[index] : null;

    return config && typeof config === 'object' ? config as Record<string, unknown> : {};
}

function getFrameIndex($frame: JQuery): number {
    const scopedIndex = $frame
        .closest('.file-preview-thumbnails')
        .children('.file-preview-frame')
        .not('.kv-zoom-thumb')
        .index($frame);

    if (scopedIndex >= 0) {
        return scopedIndex;
    }

    return $frame.closest('.file-preview').find('.file-preview-frame').index($frame);
}

function getPreviewFrame($input: JQuery<HTMLInputElement>, index: number): JQuery {
    return $input.closest('.file-input')
        .find('.file-preview-thumbnails')
        .first()
        .children('.file-preview-frame')
        .not('.kv-zoom-thumb')
        .eq(index);
}

function normalizeServerFileId(value: unknown): string {
    const fileId = String(value ?? '').trim();

    return /^[1-9]\d*$/.test(fileId) ? fileId : '';
}

function normalizePreviewUrl(value: unknown): string {
    const preview = String(value || '').trim();

    if (!preview || !preview.startsWith('<')) {
        return preview;
    }

    const doc = new DOMParser().parseFromString(preview, 'text/html');
    const media = doc.querySelector('[src], [href]');

    return media?.getAttribute('src') || media?.getAttribute('href') || '';
}

function getPreviewUrl(
    previewData: Record<string, unknown>,
    config: Record<string, unknown>,
    $frame: JQuery,
    index: number,
): string {
    const content = Array.isArray(previewData.content) ? normalizePreviewUrl(previewData.content[index]) : '';
    const configUrl = normalizePreviewUrl(config.downloadUrl);
    const frameUrl = normalizePreviewUrl($frame.find('img').attr('src'));

    return configUrl || frameUrl || content;
}

function getFrameFileId($frame: JQuery): string {
    return normalizeServerFileId($frame.attr('data-fileid'))
        || normalizeServerFileId($frame.find('.kv-file-remove').attr('data-key'));
}

function getFileId(config: Record<string, unknown>, $frame: JQuery = $()): string {
    return normalizeServerFileId(config.entityId)
        || normalizeServerFileId(config.fileId)
        || normalizeServerFileId(config.key)
        || getFrameFileId($frame);
}

function updatePreviewFrame($frame: JQuery, previewUrl: string, config: Record<string, unknown>): void {
    if (!$frame.length || !previewUrl) {
        return;
    }

    const fileId = getFileId(config);
    const filename = String(config.filename || config.caption || '');

    if (fileId) {
        $frame.attr('data-fileid', fileId);
        $frame.find('.kv-file-remove').attr('data-key', fileId);
    }

    if (filename) {
        $frame.attr('data-filename', filename);
    }

    $frame.attr('data-zoom', encodeURIComponent(previewUrl));
    $frame.find('img').first().attr('src', previewUrl);
    $frame.find('.kv-file-download').attr('href', previewUrl);
}

function isCropEnabled($input: JQuery<HTMLInputElement>): boolean {
    const enabled = $input.data('crop-enabled');

    return enabled === true || enabled === 1 || enabled === '1' || enabled === 'true';
}

function isCroppableImage(config: Record<string, unknown>, $frame: JQuery): boolean {
    const type = String(config.type || config.filetype || config.mimetype || '').toLowerCase();
    const name = String(config.caption || config.filename || '').toLowerCase();

    if (type.includes('svg') || name.endsWith('.svg')) {
        return false;
    }

    if (type.includes('image')) {
        return true;
    }

    if (/\.(jpe?g|png|webp|gif|bmp|tiff?)$/i.test(name)) {
        return true;
    }

    const imageSrc = $frame.find('img').attr('src') || '';

    return !!imageSrc && !/\.svg(?:$|\?)/i.test(imageSrc);
}

function getGalleryId($input: JQuery<HTMLInputElement>, config: Record<string, unknown>): string {
    const hiddenSelector = $input.data('hidden-input');
    const hiddenValue = hiddenSelector ? String($(hiddenSelector).val() || '') : '';
    const extra = config.extra && typeof config.extra === 'object'
        ? config.extra as Record<string, unknown>
        : {};

    return String($input.data('gallery-id') || extra.galleryId || hiddenValue || '');
}

function getGalleryName($input: JQuery<HTMLInputElement>): string {
    return String($input.data('control-name') || '');
}

function getCropUrl($input: JQuery<HTMLInputElement>): string {
    return String($input.data('crop-url') || '?do=croppImage');
}

function getRestoreUrl($input: JQuery<HTMLInputElement>): string {
    return String($input.data('restore-url') || '?do=restoreOriginal');
}

function appendSharedRequestData(
    data: FormData,
    $input: JQuery<HTMLInputElement>,
    config: Record<string, unknown>,
): void {
    data.append('galleryId', getGalleryId($input, config));
    data.append('galleryName', getGalleryName($input));
    data.append('token', String($input.data('token') || ''));
}

function hasResponseError(response: Record<string, unknown>): boolean {
    return !!response.error || !!response.errorMessage;
}

function notifyResponseError(response: Record<string, unknown>, fallback: string): void {
    notifyError(String(response.errorMessage || response.error || fallback));
}

function refreshPreviewItem(
    $input: JQuery<HTMLInputElement>,
    index: number,
    response: Record<string, unknown>,
    previousConfig: Record<string, unknown>,
): void {
    const previewData = getPreviewData($input);
    const content = Array.isArray(previewData.content) ? previewData.content : [];
    const config = Array.isArray(previewData.config) ? previewData.config : [];
    const initialPreview = Array.isArray(response.initialPreview) ? response.initialPreview : [];
    const initialPreviewConfig = Array.isArray(response.initialPreviewConfig) ? response.initialPreviewConfig : [];
    const nextContent = initialPreview[0];
    const nextConfig = initialPreviewConfig[0];

    if (!nextContent || !nextConfig || typeof nextConfig !== 'object' || index < 0) {
        updateCropButtons($input);
        return;
    }

    content[index] = nextContent;
    config[index] = {
        ...previousConfig,
        ...nextConfig as Record<string, unknown>,
        downloadUrl: (nextConfig as Record<string, unknown>).downloadUrl || nextContent,
    };

    $input.attr('data-initial-preview', JSON.stringify(content));
    $input.attr('data-initial-preview-config', JSON.stringify(config));
    $input.removeData('initial-preview');
    $input.removeData('initial-preview-config');

    updatePreviewFrame(
        getPreviewFrame($input, index),
        normalizePreviewUrl((config[index] as Record<string, unknown>).downloadUrl || nextContent),
        config[index] as Record<string, unknown>
    );
    updateCropButtons($input);
}

function uploadCroppedImage(
    croppedImage: string,
    fileId: string,
    $input: JQuery<HTMLInputElement>,
    index: number,
    config: Record<string, unknown>,
): void {
    const blob = dataURLtoBlob(croppedImage);
    const file = new File([blob], `cropped-${fileId}.webp`, { type: 'image/webp' });
    const data = new FormData();

    data.append('file', file);
    data.append('fileId', fileId);
    appendSharedRequestData(data, $input, config);

    $.nette.ajax({
        url: getCropUrl($input),
        type: 'POST',
        data,
        cache: false,
        processData: false,
        contentType: false,
        success(response: Record<string, unknown>) {
            if (hasResponseError(response)) {
                notifyResponseError(response, 'Nastala chyba při nahrávání oříznutého obrázku.');
                return;
            }

            refreshPreviewItem($input, index, response, config);
        },
        error(_xhr: unknown, _status: unknown, error: string) {
            notifyError(error || 'Nastala chyba při nahrávání oříznutého obrázku.');
        },
    });
}

function restoreOriginal(
    fileId: string,
    $input: JQuery<HTMLInputElement>,
    index: number,
    config: Record<string, unknown>,
): void {
    $.nette.ajax({
        url: getRestoreUrl($input),
        type: 'POST',
        data: {
            fileId,
            galleryId: getGalleryId($input, config),
            galleryName: getGalleryName($input),
            token: String($input.data('token') || ''),
        },
        success(response: Record<string, unknown>) {
            if (hasResponseError(response)) {
                notifyResponseError(response, 'Nepodařilo se obnovit původní obrázek.');
                return;
            }

            refreshPreviewItem($input, index, response, config);
        },
        error(_xhr: unknown, _status: unknown, error: string) {
            notifyError(error || 'Nepodařilo se obnovit původní obrázek.');
        },
    });
}

function bindCropActions($input: JQuery<HTMLInputElement>, $fileInput: JQuery): void {
    $fileInput.off(`click${CROP_ACTION_NAMESPACE}`, '.kv-file-crop');
    $fileInput.on(`click${CROP_ACTION_NAMESPACE}`, '.kv-file-crop', function(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const $frame = $(this).closest('.file-preview-frame');
        const index = getFrameIndex($frame);
        const previewData = getPreviewData($input);
        const config = getPreviewConfig(previewData, index);
        const fileId = getFileId(config, $frame);
        const imageUrl = getPreviewUrl(previewData, config, $frame, index);

        if (!fileId || !imageUrl) {
            notifyError('Nepodařilo se najít obrázek k ořezání.');
            return;
        }

        openCropper(imageUrl, croppedImage => {
            uploadCroppedImage(croppedImage, fileId, $input, index, config);
        }, 'fileinput', fileId);
    });

    $fileInput.off(`click${CROP_ACTION_NAMESPACE}`, '.kv-file-restore');
    $fileInput.on(`click${CROP_ACTION_NAMESPACE}`, '.kv-file-restore', function(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const $frame = $(this).closest('.file-preview-frame');
        const index = getFrameIndex($frame);
        const previewData = getPreviewData($input);
        const config = getPreviewConfig(previewData, index);
        const fileId = getFileId(config, $frame);

        if (!fileId) {
            notifyError('Nepodařilo se najít původní obrázek.');
            return;
        }

        restoreOriginal(fileId, $input, index, config);
    });
}

function updateCropButtons($input: JQuery<HTMLInputElement>): void {
    const previewData = getPreviewData($input);
    const enabled = isCropEnabled($input);

    $input.closest('.file-input').find('.file-preview-frame').each(function() {
        const $frame = $(this);
        if ($frame.hasClass('kv-zoom-thumb')) {
            return;
        }

        const index = getFrameIndex($frame);
        const config = getPreviewConfig(previewData, index);
        const fileId = getFileId(config, $frame);
        const hasCropped = !!config.hasCropped;
        const isImage = isCroppableImage(config, $frame);
        const $cropBtn = $frame.find('.kv-file-crop');
        const $restoreBtn = $frame.find('.kv-file-restore');

        if (!enabled || !fileId || !isImage) {
            $cropBtn.hide();
            $restoreBtn.hide();
            return;
        }

        $cropBtn.toggle(!hasCropped);
        $restoreBtn.toggle(hasCropped);
    });
}

export function openCropper(
    imageUrl: string,
    onCropCallback: (croppedImage: string) => void,
    prefix: string,
    fileId: string,
    options: CropperModalOptions = {},
): void {
    const modalId = `cropperModal-${prefix}-${fileId}`;
    const existingModal = document.getElementById(modalId);

    if (existingModal) {
        hideModal(existingModal);
        existingModal.remove();
    }

    const modalHtml = `
        <div class="comp__modal cms-cropper-modal modal fade" id="${modalId}" tabindex="-1" role="dialog" aria-hidden="true">
            <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Oříznout obrázek</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body cms-cropper-modal__body">
                        <img id="cropper-image-${prefix}-${fileId}" src="${escapeHtml(imageUrl)}" alt="" class="img-fluid cms-cropper-modal__image">
                    </div>
                    <div class="modal-footer justify-content-between">
                        <div class="btn-group" role="group">
                            <button type="button" id="rotate-left-${prefix}-${fileId}" class="btn btn-secondary"><i data-lucide="undo-2"></i></button>
                            <button type="button" id="rotate-right-${prefix}-${fileId}" class="btn btn-secondary"><i data-lucide="redo"></i></button>
                            <button type="button" id="zoom-in-${prefix}-${fileId}" class="btn btn-secondary"><i data-lucide="zoom-in"></i></button>
                            <button type="button" id="zoom-out-${prefix}-${fileId}" class="btn btn-secondary"><i data-lucide="zoom-out"></i></button>
                            <button type="button" id="reset-${prefix}-${fileId}" class="btn btn-secondary"><i data-lucide="refresh-cw"></i></button>
                        </div>
                        <div>
                            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Zrušit</button>
                            <button type="button" class="btn btn-primary" id="cropper-confirm-${prefix}-${fileId}">Oříznout</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);

    const modal = document.getElementById(modalId);
    if (!modal) {
        return;
    }

    initLucideIcons(modal);

    let cropperInstance: Cropper | null = null;
    const cropperButtons = modal.querySelectorAll<HTMLButtonElement>('.modal-footer button:not([data-bs-dismiss])');
    cropperButtons.forEach(button => button.disabled = true);

    modal.addEventListener('shown.bs.modal', () => {
        const cropperImageElement = document.getElementById(`cropper-image-${prefix}-${fileId}`) as HTMLImageElement | null;

        if (!cropperImageElement) {
            return;
        }

        cropperInstance?.destroy();
        cropperInstance = new Cropper(cropperImageElement, {
            aspectRatio: options.aspectRatio ?? NaN,
            viewMode: 0,
            scalable: true,
            zoomable: true,
            rotatable: true,
            cropBoxResizable: true,
            cropBoxMovable: true,
            dragMode: 'move',
            autoCropArea: 0.95,
            autoCrop: true,
            background: true,
            ready() {
                cropperButtons.forEach(button => button.disabled = false);
            },
        });

        bindCropperButton(`cropper-confirm-${prefix}-${fileId}`, () => {
            if (!cropperInstance) {
                return;
            }

            const canvasOptions = {
                ...(options.outputHeight ? { height: options.outputHeight } : {}),
                ...(options.outputWidth ? { width: options.outputWidth } : {}),
                imageSmoothingEnabled: true,
                imageSmoothingQuality: 'high' as ImageSmoothingQuality,
            };
            const croppedCanvas = cropperInstance.getCroppedCanvas(canvasOptions);
            if (!croppedCanvas) {
                return;
            }
            const croppedImage = croppedCanvas.toDataURL(
                options.outputMimeType || 'image/webp',
                options.outputQuality
            );

            onCropCallback(croppedImage);
            hideModal(modal);
        });
        bindCropperButton(`rotate-left-${prefix}-${fileId}`, () => cropperInstance?.rotate(-45));
        bindCropperButton(`rotate-right-${prefix}-${fileId}`, () => cropperInstance?.rotate(45));
        bindCropperButton(`zoom-in-${prefix}-${fileId}`, () => cropperInstance?.zoom(0.1));
        bindCropperButton(`zoom-out-${prefix}-${fileId}`, () => cropperInstance?.zoom(-0.1));
        bindCropperButton(`reset-${prefix}-${fileId}`, () => cropperInstance?.reset());
    }, { once: true });

    modal.addEventListener('hidden.bs.modal', () => {
        cropperInstance?.destroy();
        cropperInstance = null;
        modal.remove();

        if ($('#modalEditBlock, #modalAddBlock').filter('.show').length) {
            $('body').addClass('modal-open');
        }
    }, { once: true });

    showModal(modal);
}

function bindCropperButton(id: string, callback: () => void): void {
    const button = document.getElementById(id);

    if (button) {
        button.addEventListener('click', callback);
    }
}

function showModal(modal: HTMLElement): void {
    if (!window.bootstrap?.Modal) {
        notifyError('Bootstrap modal API není dostupné.');
        return;
    }

    window.bootstrap.Modal.getOrCreateInstance(modal).show();
}

function hideModal(modal: HTMLElement): void {
    if (!window.bootstrap?.Modal) {
        modal.remove();
        return;
    }

    window.bootstrap.Modal.getOrCreateInstance(modal).hide();
}

function notifyError(message: string): void {
    if (typeof window.throwPNotify === 'function') {
        window.throwPNotify(message, 'error');
        return;
    }

    console.error(message);
}

function escapeHtml(value: string): string {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function dataURLtoBlob(dataURL: string): Blob {
    const byteString = atob(dataURL.split(',')[1]);
    const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uintArray = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
        uintArray[i] = byteString.charCodeAt(i);
    }

    return new Blob([uintArray], { type: mimeString });
}

export function initFileInputCropper($input: JQuery<HTMLInputElement>): void {
    const $fileInput = $input.closest('.file-input');

    if (!$fileInput.length) {
        return;
    }

    bindCropActions($input, $fileInput);
    updateCropButtons($input);
}

window.initFileInputCropper = initFileInputCropper;
